import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { EligibilityMessage } from '../../../models/eligibility.models';
import { EligibilityCatalogService } from '../../../services/eligibility-catalog.service';

/**
 * Componente reusable para mostrar mensajes de elegibilidad
 * Carga el mensaje automáticamente basado en el código y locale
 */
@Component({
  selector: 'app-eligibility-message',
  standalone: false,
  templateUrl: './eligibility-message.component.html',
  styleUrls: ['./eligibility-message.component.scss']
})
export class EligibilityMessageComponent implements OnInit, OnDestroy {
  
  /** Código del mensaje a mostrar (ej: 'IROPS_NON_ELIGIBLE_MINUTES') */
  @Input() code!: string;
  
  /** Locale a usar (por defecto 'es-MX') */
  @Input() locale: string = 'es-MX';
  
  /** Mensaje fallback si no se encuentra el código */
  @Input() fallback?: EligibilityMessage;
  
  /** Emite evento cuando el usuario hace clic en el actionLabel */
  @Output() action = new EventEmitter<void>();
  
  /** Mensaje actual a mostrar */
  message?: EligibilityMessage;
  
  /** Indica si está cargando */
  isLoading = true;
  
  /** Indica si hubo un error */
  hasError = false;
  
  /** Subject para manejar unsubscribe */
  private destroy$ = new Subject<void>();

  constructor(private catalogService: EligibilityCatalogService) {}

  ngOnInit(): void {
    this.loadMessage();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Carga el mensaje desde el catálogo
   */
  private loadMessage(): void {
   
    
    if (!this.code) {
   
      this.handleError();
      return;
    }

    this.isLoading = true;
    this.hasError = false;

    this.catalogService.getMessage(this.code, this.locale)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (message) => {
          if (message) {
            this.message = message;
          } else {
            // Mensaje no encontrado, usar fallback
            this.message = this.fallback || this.getDefaultFallback();
          }
          this.isLoading = false;
        },
        error: (error) => {
          console.error(' [EligibilityMessage] Error loading message:', error);
          this.handleError();
        }
      });
  }

  /**
   * Maneja errores en la carga
   */
  private handleError(): void {
    this.hasError = true;
    this.isLoading = false;
    this.message = this.fallback || this.getDefaultFallback();
  }

  /**
   * Retorna un mensaje fallback por defecto
   */
  private getDefaultFallback(): EligibilityMessage {
    return {
      code: 'FALLBACK',
      title: this.locale.startsWith('es') ? 'Información no disponible' : 'Information not available',
      description: this.locale.startsWith('es') 
        ? 'Lo sentimos, no podemos mostrar este mensaje en este momento.'
        : 'We apologize, we cannot display this message at this time.'
    };
  }

  /**
   * Emite evento cuando se hace clic en la acción
   */
  onActionClick(): void {
    this.action.emit();
  }
}

